<template>
    <v-card>
        <v-card-title>{{$t('event.sponsor')}}</v-card-title>
        <v-card-text>
            <v-container>
                <v-row>
                    <v-col class="pt-0">
                        <v-btn color="primary" class="mr-2 mb-2" @click="showImportPhoto = true">
                            {{$t('sponsor.import_images')}}
                            <v-icon right>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn color="primary" class="mb-2" @click="handleOpenEditSponsorItem">
                            {{$t('sponsor.edit_images')}}
                            <v-icon right>mdi-pencil</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="sponsorItems && sponsorItems.length > 0">
                        <h3 class="pb-2">{{$t('sponsor.reorder')}}</h3>
                        <draggable 
                            v-model="sponsorItems"
                            :noTransitionOnDrag="false"
                            :multi-drag="true"
                            selected-class='selected'
                            ghost-class="ghost"
                            >
                            <transition-group tag="div" class="grid" name="grid">
                                <div class="gridPhotoCell" v-for="element in sponsorItems" :key="element.__isTemp ? element.__id : element.id">
                                    <v-img v-if="isImageSrcValid(element)" :src="getImageSrc(element)">
                                    </v-img>
                                </div>
                            </transition-group>
                        </draggable>
                    </v-col>
                    <v-col v-if="!(sponsorItems && sponsorItems.length > 0)">
                        <h3 color="red">{{$t('sponsor.empty')}}</h3>
                    </v-col>
                </v-row>
                <v-dialog
                    v-model="showImportPhoto"
                    width="100%"
                    persistent>
                    <v-card>
                        <v-card-title class="headline pb-5">{{$t('sponsor.import_images')}}</v-card-title>
                        <v-card-text>
                            <v-file-input accept="image/*"
                                        multiple
                                        small-chips
                                        counter
                                        show-size
                                        :label="$t('sponsor.images')"
                                        :placeholder="$t('sponsor.select_images')"
                                        v-model="listOfImportPhotos">
                            </v-file-input>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn class="mb-5 mr-5"
                                @click="handleCloseImportPhoto">
                                {{ $t('cancel') }}
                            </v-btn>
                            <v-btn class="mb-5 mr-5" color="primary" @click="batchAddSponsorItem">
                                {{$t('sponsor.import')}}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
                <v-dialog
                    v-model="showEditSponsorItem"
                    width="100%"
                    persistent>
                    <v-card>
                        <v-card-title class="headline pb-5">{{$t('sponsor.edit_images')}}</v-card-title>
                        <v-card-text>
                            <v-data-table
                                    :headers="sponsorItemHeaders"
                                    :items="editingSponsorItems"
                                    disable-sort
                                    >
                            <template v-slot:item.image="{item}">
                                <v-file-input 
                                    accept="image/*"
                                    label="Image"
                                    v-model="item.image"
                                    :clearable="false"
                                    @change="handleImageChanged(item)"
                                    >
                                    <template v-slot:selection>
                                        <v-img v-if="item && (item.src || item.image) && isImageSrcValid(item)" :src="getImageSrc(item)"
                                            max-width="200px"
                                            max-height="200px"
                                        ></v-img>
                                    </template>
                                </v-file-input>
                            </template>
                            <template v-slot:item.key="{item}">
                                <v-text-field label="Key" v-model="item.key"></v-text-field>
                            </template>
                            <template v-slot:item.type="{item}">
                                <v-text-field label="Type" v-model="item.type"></v-text-field>
                            </template>
                            <template v-slot:item.name="{item}">
                                <v-text-field label="Name" v-model="item.name"></v-text-field>
                            </template>
                            <template v-slot:item.name_tc="{item}">
                                <v-text-field label="Name TC" v-model="item.name_tc"></v-text-field>
                            </template>
                            <template v-slot:item.name_sc="{item}">
                                <v-text-field label="Name SC" v-model="item.name_sc"></v-text-field>
                            </template>
                            <!-- HTML popup inline editor -->
                            <template v-slot:item.html_desc="{value, item, header}">
                                <div @click="openInline(item, header.value)" style="cursor: pointer;">
                                    <froalaView v-model="value"></froalaView>
                                    <div v-if="!value || value === ''" style="color: grey; font-style: italic;">
                                        (Click to edit)
                                    </div>
                                </div>
                                <v-dialog
                                    :value="isEditing(item, header.value)"
                                    max-width="900px"
                                    persistent
                                    scrollable
                                >
                                    <v-card>
                                        <v-card-title>
                                            {{ header.text }}
                                        </v-card-title>
                                        <v-card-text style="max-height: 70vh;">
                                            <v-container>
                                                <froala
                                                    :config="froalaConfig"
                                                    v-model="htmlBuffer"
                                                ></froala>
                                            </v-container>
                                        </v-card-text>
                                        <v-card-actions>
                                            <v-spacer></v-spacer>
                                            <v-btn text @click="cancelInlineEdit">
                                                Cancel
                                            </v-btn>
                                            <v-btn color="primary" @click="finishInlineEdit">
                                                Edit
                                            </v-btn>
                                        </v-card-actions>
                                    </v-card>
                                </v-dialog>
                            </template>
                            <template v-slot:item.actions="{item}">
                                <v-btn color="red" dark class="ml-3" @click="deleteSponsorItem(item)">
                                    {{$t('delete')}}
                                    <v-icon right>mdi-delete</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn class="mb-2 mr-2" @click="handleCloseEditSponsorItem">
                                Close
                            </v-btn>
                            <v-btn class="mb-2 mr-2" color="primary" @click="saveEditingSponsorItems">
                                Save Edit
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-container>
        </v-card-text>
        <v-card-actions>
            <v-container>
                <v-row>
                    <v-col class="py-0 text-right">
                        <v-btn color="primary" @click="saveSponsor">
                            {{$t('save')}}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
        </v-card-actions>
        <v-overlay :z-index="999" v-model="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </v-card>

</template>

<style scoped>
    .grid {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-evenly;
        align-items: center;
        margin: 0 auto;
        max-width: 1150px;
        background: #efefef;
        border-radius: 25px;
    }

    .gridPhotoCell {
        margin: 10px !important;
        max-width: 210px;
    }

    .grid-move {
        transition: all 0.3s;
    }

    .ghost {
        opacity: 0.25;
    }

    .selected {
        background-color: #f9c7c8;
        border: solid red 2px;
    }

    .button-spacer {
        width: 10px;
    }
</style>

<script>
    import { computed, ref, watch } from "@vue/composition-api";
    import { useFind } from "feathers-vuex";
    import { DateTime } from "luxon";
    import { cloneDeep, over } from "lodash";
    import draggable from 'vuedraggable';
    import froalaConfig from "../../froala-config";

    export default {
        name: 'SponsorList',
        components: {
            draggable,
        },
        setup(props, {root}) {
            const overlay = ref(false);
            const eventId = computed(() => root.$route.params.eventId);

            // For parent
            const { Sponsor } = root.$FeathersVuex.api;
            const sponsorParams = computed(() => {
                return {
                    query: {
                        eventId: eventId.value,
                        $sort: {
                            order: 1
                        }
                    },
                }
            });
            const { items: sponsors, isPending: isSponsorPending, find } = useFind({
                model: Sponsor,
                params: sponsorParams,
            });
            const sponsorItems = ref([]);
            watch(isSponsorPending, () => {
                sponsorItems.value = sponsors.value;
            })
            const getImageSrc = (obj) => {
                if (obj) {
                    return (obj.image.src ? obj.image.src : obj.src ? obj.src : obj.image);
                }
            };
            const isImageSrcValid = (obj) => {
                const objSrc = getImageSrc(obj);
                return (objSrc && objSrc.includes('http'))
            };
            const saveSponsor = async function() {
                overlay.value = true;
                sponsorItems.value.map((item, i) => {
                    item.order = i + 1;
                })
                const promises = sponsorItems.value.map((item) => {
                    if (item.image instanceof File) {
                        return root.$uploadFile(item.image).then(id => {
                            item.image = id;
                            return item.save();
                        });
                    } else {
                        return item.save();
                    }
                });
                Promise.all(promises).then(() => {
                    find();
                    root.$store.dispatch('showSnackBar', root.$t('sponsor.save_successful'));
                }).catch((e) => {
                    root.$handleFeatherError(e);
                }).finally(() => {
                    overlay.value = false;
                });
            };

            // For import photos dialog
            const showImportPhoto = ref(false);
            const listOfImportPhotos = ref([]);
            const batchAddSponsorItem = function() {
                listOfImportPhotos.value.map((p) => {
                    const newItem = new Sponsor({eventId: eventId.value});
                    newItem.image = p;
                    if (p instanceof File)
                        newItem.src =  URL.createObjectURL(p);
                    sponsorItems.value.push(newItem);
                });
                listOfImportPhotos.value = [];
                showImportPhoto.value = false;
            };
            const handleCloseImportPhoto = () => {
                listOfImportPhotos.value = [];
                showImportPhoto.value = false;
            };

            // For HTML popup inline editor (generalized for any field)
            const htmlBuffer = ref('');
            const currentEditingItem = ref(null);
            const currentEditingField = ref(null);
            
            const isEditing = function(item, fieldName) {
                return currentEditingItem.value === item && currentEditingField.value === fieldName;
            }
            
            const openInline = function(item, fieldName) {
                // Initialize buffer with current item's field value
                htmlBuffer.value = item[fieldName] || '';
                currentEditingItem.value = item;
                currentEditingField.value = fieldName;
            }
            
            const finishInlineEdit = function() {
                // Save the buffered value back to the item when closing
                if (currentEditingItem.value && currentEditingField.value) {
                    currentEditingItem.value[currentEditingField.value] = htmlBuffer.value;
                    currentEditingItem.value = null;
                    currentEditingField.value = null;
                }
            }
            
            const cancelInlineEdit = function() {
                // Abandon buffer value and close without saving
                currentEditingItem.value = null;
                currentEditingField.value = null;
            }

            // For edit sponsor item dialog
            const showEditSponsorItem = ref(false);
            const sponsorItemHeaders = [
                {text: "Order", value: 'order', width: '50px'},
                {text: "Image", value: 'image', width: '250px'},
                {text: "Key", value: 'key'},
                {text: "Type", value: 'type'},
                {text: "Name", value: 'name'},
                {text: "Name TC", value: 'name_tc'},
                {text: "Name SC", value: 'name_sc'},
                {text: "HTML Description", value: 'html_desc'},
                {text: '', value: 'actions'},
            ];
            const editingSponsorItems = ref([]);
            const handleOpenEditSponsorItem = () => {
                editingSponsorItems.value = cloneDeep(sponsorItems.value);
                showEditSponsorItem.value = true;
            };
            const handleCloseEditSponsorItem = () => {
                showEditSponsorItem.value = false;
                editingSponsorItems.value = [];
            };
            const saveEditingSponsorItems = () => {
                overlay.value = true;
                const promises = editingSponsorItems.value.map((item) => {
                    if (item.image instanceof File) {
                        return root.$uploadFile(item.image).then(id => {
                            item.image = id;
                            return item.save();
                        });
                    } else {
                        return item.save();
                    }
                });
                Promise.all(promises).then(() => {
                    find();
                    root.$store.dispatch('showSnackBar', root.$t('sponsor.save_successful'));
                }).catch((e) => {
                    root.$handleFeatherError(e);
                }).finally(() => {
                    overlay.value = false;
                    handleCloseEditSponsorItem();
                });
            }
            const deleteSponsorItem = async function (item) {
                if (confirm(root.$t('sponsor.confirm_delete'))) {
                    overlay.value = true;
                    try {
                        await item.remove();
                        editingSponsorItems.value = editingSponsorItems.value.filter(i => i.id !== item.id);
                    } catch (e) {
                        root.$handleFeatherError(e);
                    }
                    // Handle order gap
                    editingSponsorItems.value.map((sponsor, i) => {
                        sponsor.order = i + 1;
                    })
                    const promises = editingSponsorItems.value.map((item) => {
                        return item.save();
                    });
                    Promise.all(promises).then(() => {
                        find();
                        root.$store.dispatch('showSnackBar', root.$t('sponsor.del_successful'));
                        overlay.value = false;
                    }).catch((e) => {
                        root.$handleFeatherError(e);
                    });
                }
            };
            const handleImageChanged = (item) => {
                if (item.image instanceof File)
                    item.src =  URL.createObjectURL(item.image);
            }

            return {
                froalaConfig: {
                    ...froalaConfig,
                    toolbarSticky: false,
                    zIndex: 2000
                },

                // For HTML popup inline editor
                htmlBuffer,
                isEditing,
                openInline,
                finishInlineEdit,
                cancelInlineEdit,

                isSponsorPending,

                saveSponsor,

                sponsorItemHeaders,

                sponsorItems,
                listOfImportPhotos,
                
                getImageSrc,
                isImageSrcValid,

                deleteSponsorItem,
                batchAddSponsorItem,

                showImportPhoto,    
                showEditSponsorItem,

                handleCloseImportPhoto,

                editingSponsorItems,
                handleOpenEditSponsorItem,
                handleCloseEditSponsorItem,
                saveEditingSponsorItems,
                handleImageChanged,

                DateTime,
                getFileUrl: root.$getFileURL,
                overlay,
            }
        }
    }
</script>
